import { useState, useEffect, useContext } from 'react';
import { Calendar } from 'components/inputs/date-time/Calendar';
import { IEditorProps } from 'components/Editors/Editor';
import moment from 'moment';
import { getMomentForEditor } from 'components/Editors/utils/EditorDateTimeUtils';
import { submitDateTimeValueAsString } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getFormattedDateString, getMoment } from 'utils/DateTimeUtils';
import { EditorLabel } from '../EditorLabel';
import { getUserCulture } from 'utils/UserCulture';
import { getChildValue } from 'components/property-editors/utils/ProviderNodeUtils';

interface ICalendarEditorProps extends IEditorProps {}

/**
 * CalendarEditor
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function CalendarEditor({ child, path, hideInputLabel }: ICalendarEditorProps) {
  const { editingContextId } = useContext(PropertyEditorContext);
  const childValue = getChildValue(child);
  const [currentDate, setCurrentDate] = useState<moment.Moment | undefined>(
    evaluateCalEditorValue(childValue, editingContextId, path)
  );

  useEffect(() => {
    evaluateCalEditorValue(childValue, editingContextId, path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childValue]);

  const onChange = (date?: moment.Moment) => {
    // service is expecting full `DateTimeFormat` here or will ignore as invalid:
    const dateTime = getFormattedDateString(date, getUserCulture().DateTimeFormat);
    setCurrentDate(date); // we use undefined here to clear it
    submitDateTimeValueAsString({ editingContextId, path, dateTime });
  };
  return (
    <EditorLabel editor={child} path={path} doNotWrap hideInputLabel={hideInputLabel}>
      <Calendar currentDate={currentDate} onChange={onChange} wrapperClass="dpc-calendar-editor" />
    </EditorLabel>
  );
}

function evaluateCalEditorValue(childValue: any, editingContextId: string, path: string[]) {
  const date = getMomentForEditor(childValue, true);
  if (date) {
    return date;
  } else {
    // this editor is not "nullable", so it needs to save a current date even before user interaction:
    const now = getMoment()();
    const dateTime = getFormattedDateString(now, getUserCulture().DateTimeFormat);
    // we'll pass it to the server, but we won't await that before returning:
    submitDateTimeValueAsString({ editingContextId, path, dateTime });
    return now;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}
